import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { UserTrii } from '@trii/types/dist/Users';
import { fetchUserTrii, updateEntity, uploadAvatar } from '../services/api';

export type UseImageType = {
  inputAtributes: {
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    ref: React.RefObject<HTMLInputElement>;
    type: string;
    accept: string;
  };
  action: {
    uploadImage: () => void;
    deleteImage: () => Promise<void>;
  };
  imageUrl: string;
  removeParams: (url: string) => string;
  isUploading: boolean;
  isDeleting: boolean;
};

type UseImageArgs = {
  baseUrl?: string;
  spaceId?: string;
  entityType?: 'contact' | 'business';
  entityId?: string;
  initialImageUrl?: string;
  onEntityUpdated?: (entity: unknown) => void;
};

const useImage = ({
  baseUrl,
  spaceId,
  entityType,
  entityId,
  initialImageUrl,
  onEntityUpdated,
}: UseImageArgs): UseImageType => {
  const [imageUrl, setImageUrl] = useState<string>(initialImageUrl ?? '');
  const [userTrii, setUserTrii] = useState<UserTrii | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const ref = useRef<HTMLInputElement | null>(null);

  const canRequest = useMemo(
    () => Boolean(baseUrl && spaceId && entityType && entityId),
    [baseUrl, spaceId, entityType, entityId]
  );

  function removeSasToken(url: string) {
    return url.split('?sv=')[0];
  }

  function removeCacheId(url: string) {
    return url.split('&cacheId=')[0];
  }

  function removeParams(url: string) {
    let result = removeSasToken(url);
    if (!result) return '';
    result = removeCacheId(result);
    if (!result) return '';
    return result;
  }

  const ensureUserTrii = async () => {
    if (!baseUrl || !spaceId) return null;
    const next = await fetchUserTrii({ baseUrl, spaceId });
    setUserTrii(next);
    return next;
  };

  const getImageURLWithAccessToken = async (rawUrl: string) => {
    const nextUser = userTrii ?? (await ensureUserTrii());
    const storageAzureSAS = nextUser?.storageAzureSAS as any;

    const expireAt = storageAzureSAS?.expireAt;
    const expiredAtDate = expireAt ? new Date(expireAt) : null;

    let sas = storageAzureSAS?.sas ?? '';
    if (!sas || (expiredAtDate && expiredAtDate < new Date())) {
      const refreshed = await ensureUserTrii();
      const refreshedSas = (refreshed?.storageAzureSAS as any)?.sas;
      sas = refreshedSas ?? '';
    }

    if (!sas) {
      return rawUrl;
    }

    const cacheId = `${Date.now()}`;
    return `${rawUrl}${sas}&cacheId=${cacheId}`;
  };

  const setAccessImageUrl = async (url: string) => {
    if (!url) {
      setImageUrl('');
      return;
    }

    const rawUrl = removeSasToken(url);
    if (!rawUrl) return;
    const withSas = await getImageURLWithAccessToken(rawUrl);
    setImageUrl(withSas);
  };

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!canRequest) return;

    if (ref.current) {
      ref.current.value = '';
    }

    setIsUploading(true);
    try {
      const uploadedUrl = await uploadAvatar({
        baseUrl: baseUrl!,
        spaceId: spaceId!,
        contactId: entityId!,
        file,
      });

      const updated = await updateEntity({
        baseUrl: baseUrl!,
        spaceId: spaceId!,
        entityType: entityType!,
        entityId: entityId!,
        body: { imageUrl: uploadedUrl } as any,
      });

      onEntityUpdated?.(updated);
      await setAccessImageUrl(uploadedUrl);
    } finally {
      setIsUploading(false);
    }
  };

  const uploadImage = () => {
    ref.current?.click();
  };

  const deleteImage = async () => {
    if (!canRequest) return;
    if (isDeleting) return;

    setIsDeleting(true);
    try {
       await updateEntity({
        baseUrl: baseUrl!,
        spaceId: spaceId!,
        entityType: entityType!,
        entityId: entityId!,
        body: { imageUrl: "" },
      });

      onEntityUpdated?.({ imageUrl: "" });
      setImageUrl('');
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    if (!initialImageUrl) {
      setImageUrl('');
      return;
    }

    void setAccessImageUrl(initialImageUrl);
  }, [initialImageUrl]);

  return {
    inputAtributes: {
      onChange,
      ref,
      type: 'file',
      accept: 'image/*',
    },
    action: {
      uploadImage,
      deleteImage,
    },
    imageUrl,
    removeParams,
    isUploading,
    isDeleting,
  };
};

export default useImage;
